import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

const STORAGE_KEY = 'sz_gameguide_onboarding_seen';

const slides = [
    {
        title: 'Earn XP like a pro',
        body: 'Chain correct answers, finish modules, and stack speed + first-try bonuses to level up faster.',
        tag: 'XP_SYNC',
    },
    {
        title: 'Lives keep runs intense',
        body: 'You start with 3 lives. Wrong answers cost 1, regen every 4h, or spend XP to refill instantly.',
        tag: 'LIFE_POOL',
    },
    {
        title: 'Protect your streak',
        body: 'Daily logins unlock shields and XP bursts. Keep the grind alive to maintain your multiplier path.',
        tag: 'STREAK_ENGINE',
    },
];

export default function GameGuideOnboarding() {
    const { user } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (!user) return;
        if (typeof window === 'undefined') return;

        const seen = localStorage.getItem(STORAGE_KEY);
        if (seen !== 'true') setIsOpen(true);
    }, [user]);

    const close = () => {
        setIsOpen(false);
        if (typeof window !== 'undefined') localStorage.setItem(STORAGE_KEY, 'true');
    };

    const nextSlide = () => {
        if (index === slides.length - 1) {
            close();
            return;
        }
        setIndex((prev) => prev + 1);
    };

    const prevSlide = () => setIndex((prev) => Math.max(prev - 1, 0));

    if (!isOpen) return null;

    const slide = slides[index];

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center p-6">
                    <motion.div
                        className="absolute inset-0 bg-[#050505]/84 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={close}
                    />

                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 20 }}
                        className="relative w-full max-w-2xl overflow-hidden border border-[#2d3554] bg-[#101010] shadow-[0_0_38px_rgba(140,172,255,0.15)]"
                        data-testid="game-guide-onboarding"
                    >
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(140,172,255,0.1),transparent_58%)]" />

                        <div className="relative border-b border-[#26304f] bg-[#171d2f] px-8 py-6">
                            <button
                                onClick={close}
                                className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center border border-[#2f3859] bg-[#131929] font-['IBM_Plex_Mono'] text-sm text-[#8cacff] transition-colors hover:bg-[#18203a]"
                                aria-label="Dismiss onboarding"
                            >
                                X
                            </button>
                            <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.36em] text-[#8cacff]">
                                SYSTEM HANDBOOK
                            </p>
                            <h2 className="mt-4 font-['Space_Grotesk'] text-4xl font-black uppercase italic text-white md:text-5xl">
                                {slide.title}
                            </h2>
                        </div>

                        <div className="relative px-8 py-8">
                            <div className="border border-[#242424] bg-[#151515] px-6 py-6">
                                <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.28em] text-[#00cffc]">
                                    {slide.tag}
                                </p>
                                <p className="mt-4 text-base leading-relaxed text-[#dedede]">{slide.body}</p>
                            </div>

                            <div className="mt-6 flex items-center justify-center gap-3">
                                {slides.map((entry, slideIndex) => (
                                    <span
                                        key={entry.tag}
                                        className={slideIndex === index ? 'h-[3px] w-16 bg-[#8cacff]' : 'h-[3px] w-10 bg-[#2f3859]'}
                                    />
                                ))}
                            </div>

                            <div className="mt-8 flex flex-wrap justify-center gap-4">
                                <button
                                    onClick={prevSlide}
                                    disabled={index === 0}
                                    className="inline-flex min-w-[170px] items-center justify-center border border-[#2a2a2a] bg-[#161616] px-5 py-4 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.2em] text-[#dedede] transition-colors hover:bg-[#1d1d1d] disabled:cursor-not-allowed disabled:text-[#5c5c5c]"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={nextSlide}
                                    className="inline-flex min-w-[170px] items-center justify-center border border-[#8cacff] bg-[#1a2236] px-5 py-4 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#222c49]"
                                >
                                    {index === slides.length - 1 ? 'Enter Grid' : 'Next Brief'}
                                </button>
                            </div>

                            <div className="mt-8 border border-[#242424] bg-[#151515] px-5 py-4 text-center">
                                <p className="text-sm text-[#acabaa]">
                                    Need the full systems manual?{' '}
                                    <Link to="/game-guide" className="font-['Space_Grotesk'] font-black uppercase text-[#00cffc]" onClick={close}>
                                        Open Game Guide
                                    </Link>
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
